import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';
import { Update } from '@ngrx/entity';
import { select, Store } from '@ngrx/store';
import { DatePipe } from '@angular/common';
import { User } from 'src/app/core/auth';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from 'src/app/core/reducers';
import {
  DoctorService,
  DrugService,
  Prescription,
  PrescriptionOnServerCreated,
  PrescriptionOnServerUpdated,
  selectLastCreatedDoctor,
  selectLastCreatedDrug,
  selectPrescriptionLatestSuccessfullAction,
  selectPrescriptionsActionLoading,
} from 'src/app/core/lists';
import { skip } from 'rxjs/operators';
import { CreateDrugDialogComponent } from '../../../drugs/drug-add-dialog/drug-add.dialog.component';
import { CreateDoctorDialogComponent } from '../../../doctors/doctor-add-dialog/doctor-add.dialog.component';
import { GeneralAutocompleteComponent } from '../../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { StringUtilsService } from '../../../../../../../core/services/string-utils.service';
import { MedicationHistoryComponent } from '../../../../../med-review/components/medication-history/medication-history.component';

@Component({
  selector: 'kt-prescription-edit',
  templateUrl: './prescription-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PrescriptionEditComponent implements AfterViewInit, OnInit, OnDestroy {
  prescription: Prescription;
  prescriptionForm: FormGroup;
  hasFormErrors = false;
  viewLoading = false;
  currentUser: User;
  subscriptions: Subscription[] = [];
  filteredDrugs: Observable<any[]>;
  filteredDoctors: Observable<any[]>;
  newDrug;
  newDoctor;
  isReadOnly = false;
  drugAutocompleteCall = this.drugService.searchDrugs.bind(this.drugService);
  @ViewChild('drugAutocomplete') drugAutocomplete: GeneralAutocompleteComponent;
  doctorAutocompleteCall = this.doctorService.searchDoctors.bind(this.doctorService);
  @ViewChild('doctorAutocomplete') doctorAutocomplete: GeneralAutocompleteComponent;

  ngAfterViewInit() {
    const editedPrescription = this.preparePrescription();
    if (editedPrescription.id > 0) {
      this.drugAutocomplete.setValue(this.prescription.drug);
      this.doctorAutocomplete.setValue(this.prescription.doctor);
      this.cdr.detectChanges();
    }
  }

  onDoctorChange(val) {
    this.prescriptionForm.controls.doctor.setValue(val);
    this.prescriptionForm.controls.doctor.updateValueAndValidity();
  }

  filterDrugFn(event: any): string {
    return event ? event.drug : '';
  }

  onDrugChange(val) {
    this.prescriptionForm.controls.drug.setValue(val);
    this.prescriptionForm.controls.drug.updateValueAndValidity();
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<PrescriptionEditComponent>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private translate: TranslateService,
    private drugService: DrugService,
    private doctorService: DoctorService,
    public datepipe: DatePipe,
    private cdr: ChangeDetectorRef,
    public stringUtils: StringUtilsService,
  ) {}

  ngOnInit() {
    this.isReadOnly = this.data.isReadOnly;
    this.prescription = this.data.prescription ? this.data.prescription : new Prescription();
    const doctorSub = this.store
      .pipe(select(selectLastCreatedDoctor))
      .subscribe((res) => (this.newDoctor = res));
    this.subscriptions.push(doctorSub);
    const drugSub = this.store
      .pipe(select(selectLastCreatedDrug))
      .subscribe((res) => (this.newDrug = res));
    this.subscriptions.push(drugSub);
    const loadingSub = this.store
      .pipe(select(selectPrescriptionsActionLoading))
      .subscribe((res) => (this.viewLoading = res));
    this.subscriptions.push(loadingSub);
    const successSubscription = this.store
      .pipe(select(selectPrescriptionLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          this.dialogRef.close({ added: true });
        }
      });
    this.subscriptions.push(successSubscription);
    this.createForm();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  createForm() {
    this.prescriptionForm = this.fb.group({
      drug: [this.prescription.drug, Validators.required],
      doctor: [this.prescription.doctor, Validators.required],
      prescribe_date: [this.stringUtils.getLocalDate(this.prescription.prescribe_date)],
      days_supply: [this.prescription.days_supply],
      diagnosis_code: [this.prescription.diagnosis_code],
      icd10: [this.prescription.icd10],
      refill: [this.prescription.refill],
      sig: [this.prescription.sig],
      quantity: [this.prescription.quantity],
      expiry_date: [this.stringUtils.getLocalDate(this.prescription.expiry_date)],
      stop_date: [this.stringUtils.getLocalDate(this.prescription.stop_date)],
      last_filled_date: [this.stringUtils.getLocalDate(this.prescription.last_filled_date)],
      note: [this.prescription.note, Validators.maxLength(2000)],
      hold_note: [this.prescription.hold_note],
      is_hold: [this.prescription.is_hold],
      is_active: [true],
    });
    if (this.isReadOnly) {
      this.prescriptionForm.disable();
    }
  }

  getTitle() {
    let result = this.translate.instant('PRESCRIPTION.NEW_PRESCRIPTION');
    if (!this.prescription || !this.prescription.id) {
      return result;
    }
    if (this.isReadOnly) {
      result =
        this.translate.instant('PRESCRIPTION.VIEW_PRESCRIPTION') + `-` + `${this.prescription.id}`;
      return result;
    }
    result =
      this.translate.instant('PRESCRIPTION.EDIT_PRESCRIPTION') + `-` + `${this.prescription.id}`;
    return result;
  }

  /**
   * Check control is invalid
   * @param controlName: string
   */
  isControlInvalid(controlName: string): boolean {
    const control = this.prescriptionForm.controls[controlName];
    const result = control.invalid && control.touched;
    return result;
  }

  /** ACTIONS */

  /**
   * Returns prepared prescription
   */
  preparePrescription(): Prescription {
    const controls = this.prescriptionForm.controls;
    const _prescription = new Prescription();
    _prescription.id = this.prescription.id;
    _prescription.days_supply = controls.days_supply.value;
    _prescription.drug = controls.drug.value;
    _prescription.doctor = controls.doctor.value;
    _prescription.diagnosis_code = controls.diagnosis_code.value;
    _prescription.icd10 = controls.icd10.value;
    _prescription.refill = controls.refill.value ? controls.refill.value : 0;
    _prescription.sig = controls.sig.value;
    _prescription.quantity = controls.quantity.value;
    _prescription.expiry_date = this.datepipe.transform(controls.expiry_date.value, 'yyyy-MM-dd');
    _prescription.prescribe_date = this.datepipe.transform(
      controls.prescribe_date.value,
      'yyyy-MM-dd',
    );
    _prescription.stop_date = this.datepipe.transform(controls.stop_date.value, 'yyyy-MM-dd');
    _prescription.last_filled_date = this.datepipe.transform(
      controls.last_filled_date.value,
      'yyyy-MM-dd',
    );
    _prescription.note = controls.note.value;
    _prescription.is_hold = controls.is_hold.value;
    _prescription.hold_note = controls.hold_note.value;
    _prescription.is_active = controls.is_active.value;
    for (const [key, value] of Object.entries(_prescription)) {
      if (typeof value === 'string') {
        _prescription[key] = value.trim();
      }
    }
    return _prescription;
  }

  /**
   * On Submit
   */
  onSubmit() {
    this.hasFormErrors = false;
    const controls = this.prescriptionForm.controls;
    this.drugAutocomplete.updateValueAndValidity();
    this.doctorAutocomplete.updateValueAndValidity();
    /** check form */
    if (this.prescriptionForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      this.hasFormErrors = true;
      return;
    }
    const editedPrescription = this.preparePrescription();
    if (editedPrescription.id > 0) {
      this.updatePrescription(editedPrescription);
    } else {
      this.createPrescription(editedPrescription);
    }
  }

  /**
   * Update prescription
   *
   * @param _prescription: Prescription
   */
  updatePrescription(_prescription: Prescription) {
    const updatePrescription: Update<Prescription> = {
      id: _prescription.id,
      changes: _prescription,
    };
    this.store.dispatch(
      new PrescriptionOnServerUpdated({
        partialPrescription: updatePrescription,
        prescription: _prescription,
        patientId: this.data.patientId,
      }),
    );
  }

  /**
   * Create prescription
   *
   * @param _prescription: Prescription
   */
  createPrescription(_prescription: Prescription) {
    this.store.dispatch(
      new PrescriptionOnServerCreated({
        prescription: _prescription,
        patientId: this.data.patientId,
      }),
    );
  }

  /** Alect Close prescription */
  onAlertClose() {
    this.hasFormErrors = false;
  }

  displayDrug(drug): string {
    return drug ? drug.drug : '';
  }

  displayDoctor(doctor): string {
    return doctor ? `${doctor.first_name} ${doctor.last_name}` : '';
  }

  /**
   * Rerurns status string
   */
  getStatusString(): string {
    switch (this.prescriptionForm.get('is_active').value) {
      case true:
        return this.translate.instant('GLOBAL.ACTIVE');
      case false:
        return this.translate.instant('GLOBAL.INACTIVE');
    }
    return '';
  }

  openDrugAddDialog() {
    const dialogRef = this.dialog.open(CreateDrugDialogComponent, {
      width: '35vw',
      disableClose: true,
      hasBackdrop: false,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.added) {
        this.prescriptionForm.get('drug').setValue(this.newDrug);
        this.drugAutocomplete.setValue(this.newDrug);
        this.cdr.detectChanges();
      }
    });
  }

  openDoctorAddDialog() {
    const dialogRef = this.dialog.open(CreateDoctorDialogComponent, {
      width: '35vw',
      disableClose: true,
      hasBackdrop: false,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.added) {
        this.prescriptionForm.get('doctor').setValue(this.newDoctor);
        this.doctorAutocomplete.setValue(this.newDoctor);
        this.cdr.detectChanges();
      }
    });
  }
  showHistory() {
    this.dialog.open(MedicationHistoryComponent, {
      data: { medication: this.prescription.drug },
      width: '700px',
    });
  }
}
